import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { addDays, addWeeks, format, parseISO, startOfISOWeek } from 'date-fns';
import { toast } from 'sonner';
import { z } from 'zod';
import { api, type TaskResponse } from '@/api/api';
import { surfaceApiError } from '@/lib/api-error';
import { isTaskDone } from '@/lib/status-kind';
import { meOptions } from '@/features/auth/api';
import { usePermission } from '@/features/auth/use-permission';
import {
  projectEpicsOptions,
  projectMembersOptions,
  projectTagsOptions,
  useProjectRoles,
} from '@/features/projects/api';
import {
  tasksListOptions,
  taskKeys,
  taskStatusOptions,
  useTaskStatuses,
} from '@/features/tasks/api';
import { withAncestors } from '@/features/tasks/hierarchy';
import { useDeleteTask } from '@/features/tasks/hooks';
import { timelineKeys } from '@/features/timeline/api';
import { workloadKeys, workloadOptions } from '@/features/workload/api';
import { KanbanBoard } from '@/features/tasks/components/KanbanBoard';
import { TaskStatusManager } from '@/features/tasks/components/TaskStatusManager';
import { TaskTable } from '@/features/tasks/components/TaskTable';
import { TasksToolbar } from '@/features/tasks/components/TasksToolbar';
import type { TaskView } from '@/features/tasks/components/TasksToolbar';
import { BulkActionBar } from '@/features/tasks/components/BulkActionBar';
import { EpicDrawer } from '@/features/epics/components/EpicDrawer';
import { buildUtilizationIndex } from '@/lib/overload';
import {
  DEFAULT_SORT,
  parseSort,
  serializeSort,
  sortTasks,
  toggleSort,
  type SortField,
} from '@/features/tasks/sort';
import { useTaskForm } from '@/features/tasks/task-form-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Multi-select facets can arrive as a lone value — an old single-select URL, a
// hand-typed ?tag_id=x, or a serializer that flattens a one-element array back
// to a string. Normalize to an array before validating so the filters hold.
const toArray = (v: unknown): unknown => (v === undefined ? undefined : Array.isArray(v) ? v : [v]);

// Stable identity for the Kanban branch, which never has context rows.
const EMPTY_IDS: ReadonlySet<string> = new Set();

const tasksSearchSchema = z.object({
  q: z.string().optional(),
  status_id: z.preprocess(toArray, z.array(z.string().uuid()).optional()),
  tag_id: z.preprocess(toArray, z.array(z.string()).optional()),
  epic_id: z.preprocess(toArray, z.array(z.string()).optional()),
  assignee_id: z.preprocess(toArray, z.array(z.string()).optional()),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
  // Quick filter to drop Done tasks. Combines with the Status facet via AND —
  // absent/false shows everything. `.catch` guards against hand-edited URLs.
  hide_done: z.boolean().optional().catch(undefined),
  sort: z.string().optional(),
  view: z.enum(['list', 'kanban']).optional(),
});

export type TasksSearch = z.infer<typeof tasksSearchSchema>;

export const Route = createFileRoute('/_authed/projects/$projectId/tasks')({
  validateSearch: tasksSearchSchema,
  loaderDeps: ({ search }) => ({
    date_from: search.date_from,
    date_to: search.date_to,
  }),
  loader: ({ context, params, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(meOptions()),
      context.queryClient.ensureQueryData(projectMembersOptions(params.projectId)),
      context.queryClient.ensureQueryData(projectTagsOptions(params.projectId)),
      context.queryClient.ensureQueryData(projectEpicsOptions(params.projectId)),
      context.queryClient.ensureQueryData(tasksListOptions(params.projectId, deps)),
      context.queryClient.ensureQueryData(taskStatusOptions(params.projectId)),
    ]),
  component: TasksPage,
});

function TasksPage() {
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { openEdit, openCreate } = useTaskForm();
  const canCreateTask = usePermission(projectId, 'CreateTask');

  const sort = useMemo(() => parseSort(search.sort), [search.sort]);
  const view: TaskView = search.view ?? 'list';

  // Only the date range narrows the server fetch. Status, Tag, and PIC are
  // multi-select and applied client-side below (§6.2 — client-side filtering
  // until a project crosses ~500 tasks), so every facet supports many values.
  const serverFilters = useMemo(
    () => ({
      date_from: search.date_from,
      date_to: search.date_to,
    }),
    [search.date_from, search.date_to],
  );

  const { data: me } = useSuspenseQuery(meOptions());
  const { data: members } = useSuspenseQuery(projectMembersOptions(projectId));
  const { data: tags } = useSuspenseQuery(projectTagsOptions(projectId));
  const { data: epics } = useSuspenseQuery(projectEpicsOptions(projectId));
  const { data: tasks } = useSuspenseQuery(tasksListOptions(projectId, serverFilters));
  const { statuses, statusesById } = useTaskStatuses(projectId);
  const { roles } = useProjectRoles(projectId);

  const memberDisplayNames = useMemo(() => {
    const map: Record<string, string> = {};
    if (me) map[me.id] = me.display_name;
    for (const m of members) {
      if (!map[m.user_id]) map[m.user_id] = m.display_name;
    }
    return map;
  }, [members, me]);

  const tagsById = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);
  const epicsById = useMemo(() => new Map(epics.map((e) => [e.id, e])), [epics]);
  // Unfiltered lookup so a sub-task's parent name still resolves in the Kanban
  // badge even when the parent itself is hidden by the active facet filters.
  const tasksById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);

  // Map from status UUID to sort_order for the status-column sort in TaskTable.
  const statusOrderById = useMemo(
    () => new Map(statuses.map((s) => [s.id, s.sort_order])),
    [statuses],
  );

  // Client-side, multi-select: within a facet a task passes if it matches ANY
  // selected value; it must pass every active facet. Then the real-time text
  // search. §6.2 — client-side until a project crosses ~500 tasks.
  const filtered = useMemo(() => {
    let next: TaskResponse[] = tasks.slice();

    const statusIds = new Set(search.status_id ?? []);
    if (statusIds.size > 0) next = next.filter((t) => statusIds.has(t.status_id));

    const tagIds = new Set(search.tag_id ?? []);
    if (tagIds.size > 0) next = next.filter((t) => t.tag_ids.some((id) => tagIds.has(id)));

    const epicIds = new Set(search.epic_id ?? []);
    if (epicIds.size > 0) next = next.filter((t) => epicIds.has(t.epic_id));

    const picIds = new Set(search.assignee_id ?? []);
    if (picIds.size > 0) next = next.filter((t) => t.assignees.some((a) => picIds.has(a.user_id)));

    // Hide-done is an independent AND filter — it drops Done regardless of the
    // Status facet (so Status=[Done] + hide-done intentionally yields nothing).
    if (search.hide_done) next = next.filter((t) => !isTaskDone(t.status_id, statusesById));

    const q = search.q?.trim().toLowerCase();
    if (q) {
      next = next.filter((t) => {
        if (t.name.toLowerCase().includes(q)) return true;
        const epic = epicsById.get(t.epic_id);
        return epic ? epic.name.toLowerCase().includes(q) : false;
      });
    }
    return next;
  }, [
    tasks,
    search.status_id,
    search.tag_id,
    search.epic_id,
    search.assignee_id,
    search.hide_done,
    search.q,
    epicsById,
    statusesById,
  ]);

  // Table view restores the parent chain so a matching sub-task never renders as
  // a fake root; ancestors pulled in only for structure come back in
  // `contextIds` and render dimmed. Kanban is status-partitioned — a parent
  // would land in another column — so it keeps the plain match list and relies
  // on its "Subtask of X" card badge.
  const { rows, contextIds } = useMemo(() => {
    if (view === 'kanban') return { rows: filtered, contextIds: EMPTY_IDS };
    return withAncestors(filtered, tasksById);
  }, [filtered, tasksById, view]);

  // Sorted last so `sortTasks` orders top-level rows and each parent's sibling
  // bucket alike — TaskTable's partition into buckets is stable.
  const visibleTasks = useMemo(
    () => sortTasks(rows, sort, statusOrderById),
    [rows, sort, statusOrderById],
  );

  // Workload window covering every visible task — snapped to ISO Monday on the
  // low end, +1 week beyond the latest end on the high end so all overlapping
  // weeks have a WeekLoadResponse to read. Context ancestors are real rendered
  // rows, so they need coverage too; the window only ever widens.
  const workloadWindow = useMemo(() => deriveWorkloadWindow(visibleTasks), [visibleTasks]);
  const workloadQuery = useQuery({
    ...workloadOptions({ project_id: projectId, from: workloadWindow.from, to: workloadWindow.to }),
    enabled: visibleTasks.length > 0,
  });
  const utilization = useMemo(
    () => buildUtilizationIndex(workloadQuery.data),
    [workloadQuery.data],
  );

  const updateSearch = (next: Partial<TasksSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...next }) });
  };

  // Real-time text search lives in the URL (§3.1) so it stays shareable, but
  // we replace rather than push so per-keystroke typing doesn't flood history.
  const handleSearch = (q: string) =>
    void navigate({ search: (prev) => ({ ...prev, q: q || undefined }), replace: true });
  const handleStatusFilter = (next: string[]) =>
    updateSearch({ status_id: next.length > 0 ? next : undefined });
  const handleTagFilter = (next: string[]) =>
    updateSearch({ tag_id: next.length > 0 ? next : undefined });
  const handleEpicFilter = (next: string[]) =>
    updateSearch({ epic_id: next.length > 0 ? next : undefined });
  const handlePicFilter = (next: string[]) =>
    updateSearch({ assignee_id: next.length > 0 ? next : undefined });
  const handleHideDoneChange = (next: boolean) =>
    updateSearch({ hide_done: next || undefined });
  const handleDateRange = (from: string | undefined, to: string | undefined) =>
    updateSearch({ date_from: from, date_to: to });
  const handleSort = (field: SortField) =>
    updateSearch({ sort: serializeSort(toggleSort(sort, field)) });
  const handleReset = () =>
    void navigate({
      search: (prev) => ({
        view: prev.view,
        sort: prev.sort,
      }),
      replace: true,
    });
  const handleViewChange = (next: TaskView) =>
    void navigate({ search: (prev) => ({ ...prev, view: next }), replace: true });
  const handleAddSubtask = (task: TaskResponse) =>
    openCreate({ defaultEpicId: task.epic_id, parentTask: task });

  // Inline status edit — optimistic on the active tasks-list cache, then
  // settle by invalidating the cross-view trio so Timeline and Workload
  // pick up the new status / capacity flow.
  const statusMutation = useMutation({
    mutationFn: (input: { taskId: string; statusId: string }) =>
      api.updateTaskStatus(input.taskId, { status_id: input.statusId }),
    onMutate: async ({ taskId, statusId }) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.list(projectId, serverFilters) });
      const previous = queryClient.getQueryData<TaskResponse[]>(
        taskKeys.list(projectId, serverFilters),
      );
      if (previous) {
        queryClient.setQueryData<TaskResponse[]>(
          taskKeys.list(projectId, serverFilters),
          previous.map((t) => (t.id === taskId ? { ...t, status_id: statusId } : t)),
        );
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(taskKeys.list(projectId, serverFilters), ctx.previous);
      }
      surfaceApiError(err);
    },
    onSettled: () => {
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: taskKeys.all }),
        queryClient.invalidateQueries({ queryKey: timelineKeys.all }),
        queryClient.invalidateQueries({ queryKey: workloadKeys.all }),
      ]);
    },
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openEpicId, setOpenEpicId] = useState<string | null>(null);
  const [manageStatusesOpen, setManageStatusesOpen] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const deleteMutation = useDeleteTask(() => setDeleteTaskId(null));
  const openEpic = openEpicId !== null ? (epicsById.get(openEpicId) ?? null) : null;

  const bulkMoveSprint = useMutation({
    mutationFn: async (taskIds: string[]) => {
      const taskById = new Map(tasks.map((t) => [t.id, t]));
      await Promise.all(
        taskIds.map((id) => {
          const t = taskById.get(id);
          if (!t) return Promise.resolve();
          return api.updateTask(id, {
            start_date: format(addDays(parseISO(t.start_date), 14), 'yyyy-MM-dd'),
            end_date: format(addDays(parseISO(t.end_date), 14), 'yyyy-MM-dd'),
          });
        }),
      );
    },
    onSuccess: async () => {
      toast.success('Tasks moved one sprint forward');
      setSelected(new Set());
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: taskKeys.all }),
        queryClient.invalidateQueries({ queryKey: timelineKeys.all }),
        queryClient.invalidateQueries({ queryKey: workloadKeys.all }),
      ]);
    },
    onError: surfaceApiError,
  });

  const bulkAddAssignee = useMutation({
    mutationFn: async (input: { taskIds: string[]; userId: string; estimatedMd: number }) => {
      await Promise.all(
        input.taskIds.map((id) =>
          api.addTaskAssignee(id, { user_id: input.userId, estimated_md: input.estimatedMd }),
        ),
      );
    },
    onSuccess: async () => {
      toast.success('PIC added to selection');
      setSelected(new Set());
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: taskKeys.all }),
        queryClient.invalidateQueries({ queryKey: timelineKeys.all }),
        queryClient.invalidateQueries({ queryKey: workloadKeys.all }),
      ]);
    },
    onError: surfaceApiError,
  });

  const tagOptions = useMemo(() => tags.map((t) => ({ value: t.id, label: t.name })), [tags]);
  const epicOptions = useMemo(() => epics.map((e) => ({ value: e.id, label: e.name })), [epics]);
  const picOptions = useMemo(
    () =>
      members.map((m) => ({
        value: m.user_id,
        label: memberDisplayNames[m.user_id] ?? m.display_name,
      })),
    [members, memberDisplayNames],
  );

  const selectedStatusIds: readonly string[] = search.status_id ?? [];
  const selectedTagIds = search.tag_id ?? [];
  const selectedEpicIds = search.epic_id ?? [];
  const selectedPicIds = search.assignee_id ?? [];
  const bulkBusy = bulkMoveSprint.isPending || bulkAddAssignee.isPending;

  return (
    <div className="flex h-full flex-col">
      <TasksToolbar
        searchQuery={search.q ?? ''}
        onSearchChange={handleSearch}
        statusOptions={statuses}
        selectedStatusIds={selectedStatusIds}
        onStatusChange={handleStatusFilter}
        tagOptions={tagOptions}
        selectedTagIds={selectedTagIds}
        onTagChange={handleTagFilter}
        epicOptions={epicOptions}
        selectedEpicIds={selectedEpicIds}
        onEpicChange={handleEpicFilter}
        picOptions={picOptions}
        selectedPicIds={selectedPicIds}
        onPicChange={handlePicFilter}
        hideDone={search.hide_done ?? false}
        onHideDoneChange={handleHideDoneChange}
        dateFrom={search.date_from}
        dateTo={search.date_to}
        onDateRangeChange={handleDateRange}
        view={view}
        onViewChange={handleViewChange}
        onReset={handleReset}
        onManageStatuses={() => setManageStatusesOpen(true)}
        canManageStatuses={canCreateTask}
      />
      {view === 'kanban' ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          <KanbanBoard
            projectId={projectId}
            tasks={visibleTasks}
            statuses={statuses}
            statusesById={statusesById}
            tagsById={tagsById}
            epicsById={epicsById}
            tasksById={tasksById}
            memberDisplayNames={memberDisplayNames}
            onStatusChange={(taskId, statusId) => statusMutation.mutate({ taskId, statusId })}
            onCardClick={openEdit}
            canCreateTask={canCreateTask}
            onAddSubtask={handleAddSubtask}
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto">
          <TaskTable
            projectId={projectId}
            tasks={visibleTasks}
            contextTaskIds={contextIds}
            statuses={statuses}
            statusesById={statusesById}
            roles={roles}
            epicsById={epicsById}
            tagsById={tagsById}
            memberDisplayNames={memberDisplayNames}
            selected={selected}
            onSelectChange={setSelected}
            sort={
              sort.field === DEFAULT_SORT.field && sort.direction === DEFAULT_SORT.direction
                ? DEFAULT_SORT
                : sort
            }
            onSortChange={handleSort}
            onStatusChange={(taskId, statusId) => statusMutation.mutate({ taskId, statusId })}
            onRowOpen={openEdit}
            onEpicOpen={setOpenEpicId}
            utilization={utilization}
            onDeleteTask={setDeleteTaskId}
            canCreateTask={canCreateTask}
            onAddSubtask={handleAddSubtask}
          />
        </div>
      )}
      <EpicDrawer
        projectId={projectId}
        epic={openEpic}
        tags={tags}
        onClose={() => setOpenEpicId(null)}
      />
      <TaskStatusManager
        projectId={projectId}
        open={manageStatusesOpen && canCreateTask}
        onClose={() => setManageStatusesOpen(false)}
      />
      <Dialog open={deleteTaskId !== null} onOpenChange={(open) => !open && setDeleteTaskId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete task?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTaskId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteTaskId && deleteMutation.mutate(deleteTaskId)}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkActionBar
        selectedCount={selected.size}
        picOptions={picOptions}
        onClear={() => setSelected(new Set())}
        busy={bulkBusy}
        onMoveSprint={() => bulkMoveSprint.mutate(Array.from(selected))}
        onAddAssignee={(userId, estimatedMd) =>
          bulkAddAssignee.mutate({ taskIds: Array.from(selected), userId, estimatedMd })
        }
      />
    </div>
  );
}

function deriveWorkloadWindow(tasks: readonly TaskResponse[]): { from: string; to: string } {
  if (tasks.length === 0) {
    const start = startOfISOWeek(new Date());
    return {
      from: format(start, 'yyyy-MM-dd'),
      to: format(addWeeks(start, 5), 'yyyy-MM-dd'),
    };
  }
  let earliest = tasks[0]!.start_date;
  let latest = tasks[0]!.end_date;
  for (const t of tasks) {
    if (t.start_date < earliest) earliest = t.start_date;
    if (t.end_date > latest) latest = t.end_date;
  }
  const from = startOfISOWeek(parseISO(earliest));
  const to = addWeeks(startOfISOWeek(parseISO(latest)), 1);
  return { from: format(from, 'yyyy-MM-dd'), to: format(to, 'yyyy-MM-dd') };
}
